import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
} from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { HttpLink } from "@apollo/client/link/http";
import { RetryLink } from "@apollo/client/link/retry";
import { LocalState } from "@apollo/client/local-state";

import { ErrorReporter, ErrorSeverity } from "../hooks/useErrorHandler";
import { timeoutSeconds } from "../util/constants";
import { env } from "../util/env";
import { Sans } from "../util/sdk";
import typePolicies from "./typePolicies";

export const authLink = new SetContextLink(async ({ headers }) => {
  const tokens = await Sans.sdk.core.api.tokens.freshTokens();
  return {
    credentials: "include",
    headers: {
      ...headers,
      "X-ACCESS-TOKEN": tokens?.id ? `Bearer ${tokens.id}` : "",
    },
  };
});

const createTimeoutLink = (timeoutMs: number): ApolloLink =>
  new ApolloLink((operation, forward) => {
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);

    operation.setContext(({ fetchOptions = {} }) => ({
      fetchOptions: {
        ...fetchOptions,
        signal: controller.signal,
      },
    }));

    return new Observable(observer => {
      const subscription = forward(operation).subscribe({
        next: value => observer.next(value),
        error: error => {
          globalThis.clearTimeout(timeout);
          observer.error(error);
        },
        complete: () => {
          globalThis.clearTimeout(timeout);
          observer.complete();
        },
      });

      return () => {
        globalThis.clearTimeout(timeout);
        subscription.unsubscribe();
      };
    });
  });

export function createClient(errorHandler: ErrorReporter): ApolloClient<NormalizedCacheObject> {
  const httpLink = new HttpLink({ uri: env.VITE_GRAPHQL_URI });
  const timeoutLink = createTimeoutLink(timeoutSeconds * 1000);
  const retryLink = new RetryLink({ attempts: { max: 3 } });

  const errorLink = new ErrorLink(({ error, result, operation }) => {
    if (operation.operationName === "SendTelemetry") return;

    let message: string;
    if (CombinedGraphQLErrors.is(error)) {
      message = error.errors
        .reduce((unique, error) => {
          if (!unique.includes(error.message)) {
            unique.push(error.message);
          }
          return unique;
        }, [] as string[])
        .join("\n");
      message = `Server error: ${message}`;
    } else if (error) {
      message = `Network error: ${error.message}`;
    } else {
      message = "Unknown error";
    }

    let severity = ErrorSeverity.LOW;

    if (result) {
      if (["InitCoursePageQuery"].includes(operation.operationName))
        severity = result?.data ? ErrorSeverity.MEDIUM : ErrorSeverity.HIGH;
    } else if (["InitCoursePageQuery"].includes(operation.operationName)) {
      severity = ErrorSeverity.HIGH;
    }

    // Don't report errors for certain operations during initial load
    const isInitialLoadOperation = ["MyCoursesQuery", "InitCoursePageQuery", "AllMessages"].includes(
      operation.operationName,
    );

    // Only report critical errors during initial load operations
    if (isInitialLoadOperation && severity !== ErrorSeverity.HIGH) {
      return;
    }

    errorHandler(severity, message, { ...operation });
  });

  const link = ApolloLink.from([authLink, errorLink, retryLink, timeoutLink, httpLink]);

  return new ApolloClient({
    defaultOptions: {
      query: { errorPolicy: "all" },
      mutate: { errorPolicy: "all" },
    },
    cache: new InMemoryCache({
      typePolicies,
    }),
    localState: new LocalState(),
    link,
    devtools: {
      enabled: true,
    },
  });
}
